import type { OBB, Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';
import type { Lot } from './types';

/** A building the sources draw in the Lastage timber yard: its middle, its front along the quay and its depth, in metres. */
export interface Shed {
  readonly at: Vec2;
  readonly frontage: number;
  readonly depth: number;
  /** On the strip of the spit, or at its root by the rope walks' ends. */
  readonly where: 'strip' | 'root';
}

function unit(v: Vec2): Vec2 {
  const length = Math.hypot(v[0], v[1]);
  return [v[0] / length, v[1] / length];
}

/* The sheds' source: they stand in the yard, on no plot and behind no house. */
const SHED_SOURCE = 'lastage-yard';

export function isYardShed(lot: Pick<Lot, 'sourceId'>): boolean {
  return lot.sourceId.startsWith(`${SHED_SOURCE}/`);
}

/**
 * PLAN Lots, the Lastage timber yard (2026-09-28). The painting of 1538 draws four small sheds along
 * the strip between the Geldersekade and the IJ, at 0.43, 0.53, 0.68 and 0.81 of its length from
 * where it leaves the rope-walk block, and three buildings at that root; the woodcut of 1544 draws
 * one gabled shed on the strip, 18 m from the second painted one through its georeference (21 m
 * leave-one-out error), and a row of three gabled houses at the root. The painting stands about
 * 40 px off the layout's spit, so a strip shed is placed by its share of the spit's length along the
 * lastage-geldersekade-2 quay (from (604.3, 96.8), where the quay first runs beside the yard, 241.8 m
 * to its end) and in the middle of the yard across it there; the root row stands on the woodcut's
 * foot at (629.8, 117.2), carried by the woodcut's georeference (tools/layout/woodcut_georef.py).
 * The woodcut's gabled shed is some 6.5 m wide (2.3 woodcut px a metre there); the sheds are 6 m
 * by 8 m, the root row three fronts of 5 m. Each faces the quay.
 */
export const SPIT_SHEDS: {
  readonly along: Vec2;
  readonly facing: Vec2;
  readonly sheds: readonly Shed[];
} = {
  along: unit([0.293123, 0.956077]),
  facing: unit([-0.956077, 0.293123]),
  sheds: [
    { at: [649.4, 191.7], frontage: 6, depth: 8, where: 'strip' },
    { at: [665.5, 212.1], frontage: 6, depth: 8, where: 'strip' },
    { at: [674.0, 247.4], frontage: 6, depth: 8, where: 'strip' },
    { at: [685.9, 276.7], frontage: 6, depth: 8, where: 'strip' },
    { at: [628.334, 112.42], frontage: 5, depth: 8, where: 'root' },
    { at: [629.8, 117.2], frontage: 5, depth: 8, where: 'root' },
    { at: [631.266, 121.98], frontage: 5, depth: 8, where: 'root' },
  ],
};

/** The yard's sheds as back buildings, numbered from `startIndex`; they stand on no plot, so none is anyone's. */
export function shedLots(startIndex: number): readonly Lot[] {
  const { along } = SPIT_SHEDS;
  return SPIT_SHEDS.sheds.map((shed, i): Lot => {
    const obb: OBB = { center: shed.at, axisU: along, halfU: shed.frontage / 2, halfV: shed.depth / 2 };
    return {
      index: startIndex + i,
      sourceId: `${SHED_SOURCE}/shed${i}`,
      side: 'right',
      ordinal: i,
      kind: 'backhouse',
      obb,
      corners: obbCorners(obb),
      frontage: shed.frontage,
      depth: shed.depth,
      facing: [-along[1], along[0]],
    };
  });
}
